import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminFetchPartnerInvoices,
  adminSendPartnerInvoice,
  adminMarkPartnerInvoicePaid,
  adminDeletePartnerInvoice,
} from "../../core/axios/endpoints";
import ConfirmModal from "../../components/confirm-modal";
import { lineModuleLabel } from "../../core/partner-subsidie";
import PartnerInvoiceDetailModal from "../../components/partner-invoice-detail-modal";

const fmtEUR = (n) =>
  `€ ${Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");

const STATUS_LABELS = {
  draft: { label: "Concept", color: "bg-[#091E420F] text-ink-subtle" },
  sent: { label: "Verstuurd", color: "bg-brand-subtle text-brand" },
  paid: { label: "Betaald", color: "bg-[#DCFFF1] text-[#1F845A]" },
  overdue: { label: "Achterstallig", color: "bg-[#FFF7D6] text-[#7F5F01]" },
  cancelled: { label: "Geannuleerd", color: "bg-[#FFECEB] text-[#AE2A19]" },
};

const TYPE_LABELS = {
  invoice: { label: "Factuur", color: "bg-[#091E420F] text-ink-subtle" },
  credit_note: { label: "Creditnota", color: "bg-[#FFECEB] text-[#AE2A19]" },
};

// Interne administratie: een verstuurde of betaalde factuur mag nog
// gecorrigeerd worden. Creditnota's, geannuleerde en (deels) gecrediteerde
// facturen niet.
const isEditable = (row) =>
  row.type === "invoice" &&
  row.status !== "cancelled" &&
  !(row.creditedAmount > 0);

const Badge = ({ map, value }) => {
  const cfg = map[value] ?? { label: value, color: "bg-[#091E420F] text-ink-subtle" };
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const AdminPartnerFacturenPage = () => {
  const [data, setData] = useState({ docs: [], pagination: {}, totals: {} });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pageError, setPageError] = useState(null);

  const [detailId, setDetailId] = useState(null);
  const [detailEdit, setDetailEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [markPaidRow, setMarkPaidRow] = useState(null);
  const [markPaidDate, setMarkPaidDate] = useState(
    new Date().toISOString().slice(0, 10),
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const body = await adminFetchPartnerInvoices({
        page,
        limit: 25,
        status,
        type,
        search,
      });
      const payload = body?.data ?? body;
      setData({
        docs: payload?.docs ?? [],
        pagination: payload?.pagination ?? {},
        totals: payload?.totals ?? {},
      });
    } catch (e) {
      console.error("Partner-facturen laden mislukt:", e);
    } finally {
      setLoading(false);
    }
  }, [page, status, type, search]);

  useEffect(() => {
    refresh();
  }, [refresh, reloadKey]);

  const onSend = async (id) => {
    setPageError(null);
    setBusyId(id);
    try {
      await adminSendPartnerInvoice(id, { resend: true });
      setReloadKey((k) => k + 1);
    } catch (e) {
      setPageError(e?.response?.data?.error ?? "Versturen mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const onTogglePaid = async (row) => {
    setPageError(null);
    if (row.status === "paid") {
      // Undo direct (geen datum-prompt nodig).
      setBusyId(row._id);
      try {
        await adminMarkPartnerInvoicePaid(row._id, { undo: true });
        setReloadKey((k) => k + 1);
      } catch (e) {
        setPageError(e?.response?.data?.error ?? "Status wijzigen mislukt.");
      } finally {
        setBusyId(null);
      }
      return;
    }
    // Markeer betaald: open mini-modal voor betaaldatum (default vandaag).
    setMarkPaidDate(new Date().toISOString().slice(0, 10));
    setMarkPaidRow(row);
  };

  const confirmMarkPaid = async () => {
    if (!markPaidRow) return;
    setBusyId(markPaidRow._id);
    try {
      // Stuur datum als ISO-string; server slaat 'm op als Date.
      // Mensen vullen meestal alleen dag in - zet 'm op 12:00 UTC zodat
      // tijdzone-shifts (NL = UTC+2 in DST) niet alsnog gisteren tonen.
      const paidAt = new Date(`${markPaidDate}T12:00:00.000Z`).toISOString();
      await adminMarkPartnerInvoicePaid(markPaidRow._id, { paidAt });
      setMarkPaidRow(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setPageError(e?.response?.data?.error ?? "Markeren mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const onDeleteDraft = async () => {
    if (!deleteId) return;
    setPageError(null);
    setBusyId(deleteId);
    try {
      await adminDeletePartnerInvoice(deleteId);
      setDeleteId(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setDeleteId(null);
      setPageError(e?.response?.data?.error ?? "Verwijderen mislukt.");
    } finally {
      setBusyId(null);
    }
  };

  const t = data.totals ?? {};

  return (
    <div className="page-wrapper px-8 pt-20 pb-12">
      {pageError ? (
        <div
          role="alert"
          className="mb-4 flex items-start justify-between gap-3 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]"
        >
          <span>{pageError}</span>
          <button
            type="button"
            onClick={() => setPageError(null)}
            className="shrink-0 text-xs font-medium hover:underline"
          >
            Sluiten
          </button>
        </div>
      ) : null}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
            <Link to="/panel/admin/dashboard" className="hover:underline">Dashboard</Link>
            <span className="mx-1">/</span>
            <span>BTW-teruggave</span>
            <span className="mx-1">/</span>
            <span>Partner-facturen</span>
          </nav>
          <h1 className="text-2xl font-medium text-ink">
            Partner-facturen
          </h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            <span className="font-medium text-ink">{t.count ?? 0}</span> facturen
          </p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded bg-white p-4 shadow-card">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Open</p>
          <p className="mt-2 text-2xl font-medium tabular-nums text-[#7F5F01]">{fmtEUR(t.openIncl)}</p>
          <p className="mt-1 text-xs text-ink-subtlest">Verstuurd, nog niet betaald</p>
        </div>
        <div className="rounded bg-white p-4 shadow-card">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Betaald</p>
          <p className="mt-2 text-2xl font-medium tabular-nums text-brand">{fmtEUR(t.paidIncl)}</p>
          <p className="mt-1 text-xs text-ink-subtlest">Ontvangen van partners</p>
        </div>
        <div className="rounded bg-white p-4 shadow-card">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Gecrediteerd</p>
          <p className="mt-2 text-2xl font-medium tabular-nums text-[#AE2A19]">{fmtEUR(t.creditedIncl)}</p>
          <p className="mt-1 text-xs text-ink-subtlest">Via creditnota&apos;s</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
        >
          <option value="">Alle statussen</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          className="rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
        >
          <option value="">Facturen + creditnota&apos;s</option>
          <option value="invoice">Alleen facturen</option>
          <option value="credit_note">Alleen creditnota&apos;s</option>
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op nummer, kenmerk, naam, email…"
          className="flex-1 min-w-[220px] rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
        />
      </div>

      {/* Tabel */}
      <div className="rounded bg-white shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-sunken text-left text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
            <tr>
              <th className="px-3 py-2">Nummer</th>
              <th className="px-3 py-2">Partner</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Totaal</th>
              <th className="px-3 py-2 text-right">Acties</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="border-t border-line">
                <td colSpan={7} className="px-3 py-10 text-center text-sm text-ink-subtlest">
                  Laden…
                </td>
              </tr>
            ) : data.docs.length === 0 ? (
              <tr className="border-t border-line">
                <td colSpan={7} className="px-3 py-10 text-center text-sm text-ink-subtlest">
                  Geen partner-facturen gevonden.
                </td>
              </tr>
            ) : (
              data.docs.map((row) => (
                <tr
                  key={row._id}
                  className="cursor-pointer border-t border-line hover:bg-sunken"
                  onClick={() => {
                    setDetailEdit(false);
                    setDetailId(row._id);
                  }}
                >
                  <td className="px-3 py-2 text-xs font-medium text-ink-subtlest">
                    <span className="text-sm font-medium text-ink">{row.invoiceNumber}</span>
                    {/* Welke modules op de factuur staan (BTW / subsidie). */}
                    <div className="mt-0.5 text-[11px] font-normal text-ink-subtlest">
                      {Array.from(
                        new Set((row.lines ?? []).map((l) => lineModuleLabel(l))),
                      ).join(" · ")}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <div className="font-medium text-ink">
                      {row.partnerUserId?.bedrijfsnaam ??
                        row.partnerUserId?.naam ??
                        "-"}
                    </div>
                    <div className="text-xs text-ink-subtlest">{row.billingEmail}</div>
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <Badge map={TYPE_LABELS} value={row.type} />
                  </td>
                  <td className="px-3 py-2 text-sm text-ink-subtle">
                    {fmtDate(row.invoiceDate)}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <Badge map={STATUS_LABELS} value={row.status} />
                  </td>
                  <td className="px-3 py-2 text-right text-sm tabular-nums font-medium text-ink">
                    {fmtEUR(row.totalIncl)}
                  </td>
                  <td
                    className="px-3 py-2 text-right text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {row.status === "draft" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => onSend(row._id)}
                            disabled={busyId === row._id}
                            className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Verstuur
                          </button>
                          <button
                            type="button"
                            onClick={() => onTogglePaid(row)}
                            disabled={busyId === row._id}
                            title="Markeer als betaald zonder te versturen (bijv. factuur loopt via externe partij)"
                            className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Markeer betaald
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteId(row._id)}
                            disabled={busyId === row._id}
                            className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-[#AE2A19] hover:bg-[#FFECEB] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Verwijder
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => onTogglePaid(row)}
                            disabled={busyId === row._id || row.status === "cancelled"}
                            className={
                              row.status === "paid"
                                ? "inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                                : "inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                            }
                          >
                            {row.status === "paid"
                              ? "Markeer onbetaald"
                              : "Markeer betaald"}
                          </button>
                          {isEditable(row) ? (
                            <button
                              type="button"
                              onClick={() => {
                                setDetailEdit(true);
                                setDetailId(row._id);
                              }}
                              disabled={busyId === row._id}
                              title="Regels aanpassen, weghalen of vervangen door een andere aanvraag"
                              className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Bewerk
                            </button>
                          ) : null}
                          {isEditable(row) && row.status !== "paid" ? (
                            <button
                              type="button"
                              onClick={() => setDeleteId(row._id)}
                              disabled={busyId === row._id}
                              className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-[#AE2A19] hover:bg-[#FFECEB] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Verwijder
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.pagination?.totalPages > 1 ? (
        <div className="mt-4 flex items-center justify-between text-sm text-ink-subtle">
          <span>
            Pagina {data.pagination.page} van {data.pagination.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Vorige
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= (data.pagination.totalPages ?? 1) || loading}
              className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Volgende
            </button>
          </div>
        </div>
      ) : null}

      {detailId ? (
        <PartnerInvoiceDetailModal
          isOpen={!!detailId}
          invoiceId={detailId}
          initialEdit={detailEdit}
          onClose={() => {
            setDetailId(null);
            setDetailEdit(false);
          }}
          onChanged={() => {
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      <ConfirmModal
        isOpen={!!deleteId}
        variant="danger"
        title="Factuur verwijderen?"
        message="De factuur wordt verwijderd en de gekoppelde aanvragen komen terug op te factureren. Het factuurnummer wordt niet opnieuw uitgegeven. Een betaalde factuur zet je eerst op onbetaald."
        confirmLabel="Verwijder factuur"
        cancelLabel="Annuleren"
        busy={!!busyId}
        onConfirm={onDeleteDraft}
        onCancel={() => setDeleteId(null)}
      />

      {/* Markeer-betaald modal met datum-input. Default = vandaag, maar je
          kunt de echte datum invullen als de betaling al eerder binnenkwam. */}
      {markPaidRow ? (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#091E428A] p-4">
          <div className="w-full max-w-md rounded-md bg-white shadow-overlay">
            <h3 className="px-6 pt-6 text-xl font-medium text-ink">
              Markeer als betaald
            </h3>
            <div className="px-6 py-4 text-sm text-ink">
              <p className="text-sm text-ink-subtlest">
                Factuur <span className="font-medium text-ink">{markPaidRow.invoiceNumber}</span> &middot;{" "}
                {fmtEUR(markPaidRow.totalIncl)}
              </p>
              <label className="mt-4 block text-sm">
                <span className="text-xs font-semibold text-ink-subtlest">
                  Datum waarop betaald is
                </span>
                <input
                  type="date"
                  value={markPaidDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setMarkPaidDate(e.target.value)}
                  className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
                  disabled={!!busyId}
                />
                <span className="mt-1 block text-xs text-ink-subtlest">
                  Standaard vandaag, pas aan als de betaling eerder binnenkwam.
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2 px-6 pb-6">
              <button
                type="button"
                onClick={() => setMarkPaidRow(null)}
                disabled={!!busyId}
                className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmMarkPaid}
                disabled={!!busyId || !markPaidDate}
                className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busyId ? "Bezig..." : "Bevestig"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminPartnerFacturenPage;
